import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { formatDate, relativeTime } from "../lib/format";
import { Button, ConfirmButton, EmptyState, ErrorState, Input, PageHeader, Spinner, StatCard, Table, Td } from "../components/ui";

interface SessionRow {
  id: string;
  created_at: string;
  expires_at: string;
  email: string;
  name: string | null;
  token_version: number;
}

export default function AuthSessions() {
  const { hasRole } = useAuth();
  const canWrite = hasRole("admin");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const { data, loading, error, reload } = useApi<{
    sessions: SessionRow[];
    counts: { active_refresh: number; pending_exchange: number };
  }>(`/auth-sessions?search=${encodeURIComponent(query)}`);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function revoke(id: string) {
    setBusy(true);
    setActionError(null);
    try {
      await api.delete(`/auth-sessions/${id}`);
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Auth sessions"
        subtitle="Active refresh tokens in the auth worker. Revoking one signs that device out at its next refresh."
      />

      {data && (
        <div className="mb-4 grid grid-cols-2 gap-3 md:max-w-md">
          <StatCard label="Active sessions" value={data.counts.active_refresh} />
          <StatCard label="Pending exchanges" value={data.counts.pending_exchange} hint="unredeemed login handoffs" />
        </div>
      )}

      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(search);
        }}
      >
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by email…" className="max-w-sm" />
        <Button type="submit" variant="secondary">
          Filter
        </Button>
      </form>

      {actionError && <ErrorState message={actionError} />}

      {loading ? (
        <Spinner />
      ) : error || !data ? (
        <ErrorState message={error ?? "Failed to load"} onRetry={reload} />
      ) : data.sessions.length === 0 ? (
        <EmptyState message="No active sessions." />
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <Table headers={["User", "Created", "Expires", canWrite ? "" : null].filter((h) => h !== null)}>
            {data.sessions.map((s) => (
              <tr key={s.id}>
                <Td>
                  <Link to={`/users/${encodeURIComponent(s.email)}`} className="text-rail-600 hover:underline">
                    {s.email}
                  </Link>
                </Td>
                <Td className="text-xs" title={formatDate(s.created_at)}>
                  {relativeTime(s.created_at)}
                </Td>
                <Td className="text-xs">{formatDate(s.expires_at)}</Td>
                {canWrite && (
                  <Td>
                    <ConfirmButton
                      label="Revoke"
                      confirmLabel="Revoke session?"
                      disabled={busy}
                      onConfirm={() => revoke(s.id)}
                    />
                  </Td>
                )}
              </tr>
            ))}
          </Table>
        </div>
      )}
    </div>
  );
}
